/* eslint-disable no-restricted-syntax */
import { SpeakerHighIcon, SpeakerSlashIcon } from '@phosphor-icons/react';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * Sound for the post editor's preview.
 *
 * Feed previews autoplay muted with no controls, which is how the platforms behave but
 * leaves an author unable to hear the clip they are about to publish. The post editor
 * (inspector) wraps its preview in `PreviewSoundProvider`; every video `MediaSlot` inside
 * then shows a speaker toggle and follows one shared on/off state. Anywhere without the
 * provider (ad previews, review decks, calendar, style guide) nothing changes: the feed-style
 * muted loop with no controls. Off by default, so opening a post never plays audio unasked.
 */
interface PreviewSoundState {
  soundOn: boolean;
  setSoundOn: (next: boolean) => void;
}

const PreviewSoundContext = createContext<PreviewSoundState | null>(null);

export function PreviewSoundProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [soundOn, setSoundOn] = useState(false);
  const value = useMemo(() => ({ soundOn, setSoundOn }), [soundOn]);
  return <PreviewSoundContext.Provider value={value}>{children}</PreviewSoundContext.Provider>;
}

/** Null outside the post editor, where previews stay silent and control-free. */
export function usePreviewSound(): PreviewSoundState | null {
  return useContext(PreviewSoundContext);
}

/**
 * How the slot gets its height. Exactly one of these must be supplied — the
 * union exists so that "my parent sizes me" is a DECLARATION, not an omission.
 *
 * Every child of this component is `absolute inset-0`, so a slot with neither a
 * ratio nor a sized parent has no in-flow content and collapses to height 0,
 * taking the image with it and reporting no error anywhere. That is exactly how
 * BUG-PREVIEW-ORIGINAL-ASPECT-COLLAPSE-001 stayed invisible across ~1,189
 * posts. Making `ratio` optional would let a new caller reintroduce it with a
 * clean typecheck, a clean lint and a green suite, so it is not optional.
 */
type MediaSlotSizing =
  /** Slot sizes itself from `aspect-ratio` (width / height). */
  | { ratio: number; fill?: never }
  /** Slot is absolutely positioned inside an already-sized parent. */
  | { ratio?: never; fill: true };

interface MediaSlotBaseProps {
  imageUrl?: string | null;
  videoUrl?: string | null;
  videoPoster?: string | null;
  alt?: string;
  isLoading?: boolean;
  /**
   * Focal point of the cover-crop, 0..1 (0.5 = centre). Maps directly to CSS
   * `object-position`, which for an `object-cover` image is the exact same crop
   * the publish-time `computeCrop` bakes at zoom 1 — so the preview never lies.
   * Defaults to centre.
   */
  focalX?: number;
  focalY?: number;
  /** Zoom ≥ 1 (1 = fill). Scales the cover image around the focal point. */
  zoom?: number;
  /** Background tone for the empty/skeleton state. */
  bg?: 'muted' | 'black' | 'zinc';
  /** Replaces the empty-state placeholder text when no media is provided. */
  emptyHint?: string;
  /** Class names forwarded to the outer container. */
  className?: string;
  /** Class names forwarded to the rendered img/video element. */
  mediaClassName?: string;
  /**
   * Whether the platform is video-first (TikTok / YouTube Short). Forces
   * the empty-state hint into the "video" copy and uses dark zinc bg.
   */
  videoFirst?: boolean;
  /** Optional overlay rendered above the media (controls, gradient, etc). */
  overlay?: React.ReactNode;
  /**
   * If true, video plays inline silently and loops (Instagram/TikTok feed
   * behaviour). When false, native controls are shown (e.g. clicking from a
   * detail view). Default: true.
   */
  autoplay?: boolean;
}

type MediaSlotProps = MediaSlotBaseProps & MediaSlotSizing;

/**
 * The only ratio values CSS `aspect-ratio` can actually use. `NaN`, `0`,
 * `Infinity` and negatives all produce either an invalid declaration or a
 * degenerate box — in every case height 0, i.e. the original bug wearing a
 * different hat. Verified in a browser: `aspect-ratio: NaN` computes to `auto`
 * and `aspect-ratio: 0 / 1` computes to `0 / 1`; both render zero-height.
 *
 * A caller that gets here with a bad number has a real problem, but silently
 * vanishing media is the worst possible way to express it, so we degrade to a
 * square — visibly wrong beats invisibly absent.
 */
const FALLBACK_RATIO = 1;
function usableRatio(ratio: number | undefined): number | undefined {
  if (ratio === undefined) return undefined;
  return Number.isFinite(ratio) && ratio > 0 ? ratio : FALLBACK_RATIO;
}

const BG_CLASSES: Record<NonNullable<MediaSlotBaseProps['bg']>, string> = {
  muted: 'bg-muted',
  black: 'bg-black',
  zinc: 'bg-zinc-900',
};

const EMPTY_TEXT_COLORS: Record<NonNullable<MediaSlotBaseProps['bg']>, string> = {
  muted: 'text-muted-foreground',
  black: 'text-white/60',
  zinc: 'text-white/60',
};

/**
 * Canonical media slot for every platform-preview replica. Renders, in priority
 * order: video → image → loading skeleton → empty-state placeholder.
 *
 * Video gets the in-feed treatment by default (autoplay muted loop, no
 * controls, plays inline) so the preview matches the actual platform feed
 * experience pixel-for-pixel. Pass `autoplay={false}` to surface native
 * controls instead — useful when the preview lives inside a detail dialog.
 */
export const MediaSlot: React.FC<MediaSlotProps> = ({
  imageUrl,
  videoUrl,
  videoPoster,
  alt,
  isLoading,
  ratio,
  fill,
  focalX,
  focalY,
  zoom,
  bg = 'muted',
  emptyHint,
  className,
  mediaClassName,
  videoFirst = false,
  overlay,
  autoplay = true,
}) => {
  // A broken/404 image URL must NOT spill the browser's native broken-`<img>`
  // glyph + alt text into the media region — fall back to the clean empty
  // placeholder instead. Reset when the source changes so a new URL gets a
  // fresh attempt.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  const sound = usePreviewSound();
  const videoRef = useRef<HTMLVideoElement>(null);
  const soundOn = sound?.soundOn ?? false;
  // React applies `muted` only when the element mounts, so a later change has to be set on the
  // element itself. Unmuting happens inside the toggle's click, which browsers treat as consent.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !autoplay) return;
    el.muted = !soundOn;
    if (soundOn && el.paused) void el.play().catch(() => undefined);
  }, [soundOn, autoplay, videoUrl]);

  // Validated HERE, in the renderer, not upstream in `resolveAspectRatio`.
  // That helper is a convention callers may follow; this is the invariant.
  // Several call sites pass a literal ratio without going through it at all.
  const safeRatio = usableRatio(ratio);

  const showLoading = !!isLoading;
  const showVideo = !showLoading && !!videoUrl;
  const showImage = !showLoading && !showVideo && !!imageUrl && !imageFailed;
  const showEmpty = !showLoading && !showVideo && !showImage;
  const fallbackHint = emptyHint ?? (videoFirst ? 'Add video to preview' : 'Add media to preview');

  // CSS object-position drives the focal cover-crop; transform: scale zooms in
  // around that same focal point. Together they match the publish-time
  // computeCrop bake. Only set when framing differs from the centre-fill default
  // so existing previews are untouched.
  const fx = focalX ?? 0.5;
  const fy = focalY ?? 0.5;
  const z = Math.max(1, zoom ?? 1);
  const isFramed = focalX != null || focalY != null || z > 1;
  const framingStyle: React.CSSProperties | undefined = isFramed
    ? {
        objectPosition: `${fx * 100}% ${fy * 100}%`,
        ...(z > 1
          ? { transform: `scale(${z})`, transformOrigin: `${fx * 100}% ${fy * 100}%` }
          : {}),
      }
    : undefined;

  return (
    // overflow-hidden: zoom > 1 scales the image via `transform`, which without
    // clipping paints past this box into whatever chrome sits above/below it
    // (verified live — at max zoom the header and caption were fully covered).
    // Every consumer's OWN decorative chrome (TikTok's avatar badge, "For You"
    // underline, etc.) is a sibling of MediaSlot inside an already-clipped
    // parent, never a descendant here, so this can't clip anyone else's UI.
    // The aspect ratio is an inline style, NOT a utility class: it is a runtime
    // number (often a probed float like 1.7777777777777777), and Tailwind can
    // only emit utilities whose exact text exists in source at build time.
    // See resolveAspectRatio in ./types.
    <div
      // `fill` OWNS the positioning — it does not merely describe it. Declaring
      // `fill` and then relying on each caller to hand-write `absolute inset-0`
      // would leave the collapse one forgotten className away, which is the
      // whole failure mode this union exists to close.
      // Positioning goes LAST, after `className`. `cn` is tailwind-merge, so
      // whatever comes last wins its group — with `className` last, a caller
      // passing `relative`/`static`/`top-0` would silently strip `fill`'s
      // `absolute inset-0` and collapse the slot: the exact failure the union
      // exists to prevent, reachable while fully typechecking. The sizing
      // contract is not the caller's to override.
      className={cn(
        'overflow-hidden',
        BG_CLASSES[bg],
        className,
        fill ? 'absolute inset-0' : 'relative',
      )}
      style={safeRatio !== undefined ? { aspectRatio: safeRatio } : undefined}
    >
      {showLoading ? (
        // House shimmer convention (src/components/ui/skeleton.tsx): a `before`
        // pseudo-element sweeps a translated gradient across the tinted base so
        // the loading state reads as "in progress" rather than a dead flat box.
        // Global prefers-reduced-motion CSS (index.css) neutralizes the sweep —
        // no motion-safe: prefix needed, matching Skeleton's own convention.
        <div
          className={cn(
            'absolute inset-0 overflow-hidden',
            BG_CLASSES[bg],
            'before:absolute before:inset-0 before:-translate-x-full',
            'before:animate-[shimmer_1.6s_infinite]',
            'before:bg-gradient-to-r before:from-transparent before:to-transparent',
            bg === 'muted'
              ? 'before:via-foreground/15 dark:before:via-foreground/10'
              : 'before:via-white/10',
          )}
        />
      ) : null}

      {showVideo ? (
        <video
          ref={videoRef}
          src={videoUrl ?? undefined}
          poster={videoPoster ?? undefined}
          autoPlay={autoplay}
          muted={autoplay}
          loop={autoplay}
          playsInline
          controls={!autoplay}
          preload="metadata"
          className={cn('absolute inset-0 size-full object-cover', mediaClassName)}
          aria-label={alt}
        />
      ) : null}

      {showVideo && sound && autoplay ? (
        <button
          type="button"
          aria-pressed={soundOn}
          aria-label={soundOn ? 'Turn preview sound off' : 'Turn preview sound on'}
          title={soundOn ? 'Turn sound off' : 'Turn sound on'}
          data-preview-sound-toggle="true"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            sound.setSoundOn(!soundOn);
          }}
          className="absolute left-2 top-2 z-30 flex size-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        >
          {soundOn ? (
            <SpeakerHighIcon size={16} weight="regular" aria-hidden />
          ) : (
            <SpeakerSlashIcon size={16} weight="regular" aria-hidden />
          )}
        </button>
      ) : null}

      {showImage ? (
        <img
          src={imageUrl ?? undefined}
          alt={alt ?? ''}
          onError={() => setImageFailed(true)}
          style={framingStyle}
          className={cn('absolute inset-0 size-full object-cover', mediaClassName)}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          // Lets PostBody's reframe-drag gate target THIS image specifically —
          // a bare `tagName === 'IMG'` check also matches the tiny header
          // avatar rendered inside the same reframe wrapper, computing overflow
          // from a 32px box and snapping the photo on any drag that starts
          // near the avatar (verified live: a 10px move produced an extreme jump).
          data-media-slot-photo="true"
        />
      ) : null}

      {showEmpty ? (
        <div className={cn('absolute inset-0 flex items-center justify-center', BG_CLASSES[bg])}>
          <span className={cn('text-detail', EMPTY_TEXT_COLORS[bg])}>{fallbackHint}</span>
        </div>
      ) : null}

      {overlay}
    </div>
  );
};
